import dayjs from 'dayjs';

import useTimeZone from 'hooks/use-timezone';
import { useMemo } from 'react';
import './lockable.scss';

interface LocableDateInputProps<T extends dayjs.ConfigType> {
  id?: string;
  label?: string;
  max?: T;
  min?: T;
  type?: 'date' | 'datetime-local';
  date?: T;
  setDate: (iso?: string) => void;
  ['data-cy']?: string;
}

const LockableDateInput = <T extends dayjs.ConfigType>({
  date,
  setDate,
  id = 'date-input',
  label,
  max,
  min,
  type = 'date',
  ...props
}: LocableDateInputProps<T>) => {
  const { dateInputToUtc, dateTimeLocalToUtc, newZonedDayJs, utcToZonedDate, utcToZonedDateTimeLocal } = useTimeZone();

  const parseDate = useMemo(() => {
    return type === 'datetime-local' ? utcToZonedDateTimeLocal : utcToZonedDate;
  }, [type, utcToZonedDate, utcToZonedDateTimeLocal]);

  const getDate = useMemo(() => {
    return type === 'datetime-local' ? dateTimeLocalToUtc : dateInputToUtc;
  }, [type, dateInputToUtc, dateTimeLocalToUtc]);

  return (
    <div className="d-flex">
      <div className="flex-fill text-field">
        <input
          className="h-100 w-100"
          type={type}
          id={id}
          name="date"
          disabled={!date}
          max={!max ? undefined : parseDate(max)}
          min={!min ? undefined : parseDate(min)}
          value={parseDate(date)}
          onChange={e => setDate(getDate(e.target.value))}
          data-cy={props['data-cy']}
        />
        {!!label && <label htmlFor={id}>{label}</label>}
      </div>
      <label id="date-lock" className="p-relative d-flex align-items-center">
        <span className="icon-large material-icons">{!date ? 'lock' : 'lock_open'}</span>
        <input
          type="checkbox"
          className="p-absolute w-100"
          checked={!!date}
          onChange={e => setDate(e.target.checked ? newZonedDayJs().toISOString() : undefined)}
        />
      </label>
    </div>
  );
};

export default LockableDateInput;
